import { createRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, PackageCheck, RotateCcw, Tag, Wallet } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopMessages } from "./desktop/messages.tsx";
import { SubPage } from "../components/layout.tsx";
import { Empty, ErrorBlock, Spinner } from "../components/ui.tsx";
import { Card } from "@/components/ui/card";
import { api } from "../api/client.ts";
import { useMe } from "../api/queries.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  站内消息
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **要紧的事不能只躺在它自己那一页里。**
 *    上游调价的提醒原先只出现在「我的定价」页顶部 —— 商家得先想起来切身份、
 *    再点进两层才看得到。而那条提醒说的是"你有 3 个规格已经在亏本卖"，
 *    看不到就一直在亏。
 */

interface Message {
  id: number;
  orgId: number | null;
  kind: string;
  title: string;
  body: string;
  link: string;
  readAt: string | null;
  createdAt: string;
}

/** ⚠️ 认不出的类型给个中性图标，不要让整页崩掉 —— 以后加新类型是常事。 */
const ICON: Record<string, LucideIcon> = {
  price_change: AlertTriangle,
  product_reviewed: Tag,
  order: PackageCheck,
  refund: RotateCcw,
  withdrawal: Wallet,
};

const TONE: Record<string, string> = {
  price_change: "text-amber-600 bg-amber-50",
  product_reviewed: "text-brand-600 bg-brand-50",
  order: "text-emerald-600 bg-emerald-50",
  refund: "text-sky-600 bg-sky-50",
  withdrawal: "text-brand-600 bg-brand-50",
};

/** ⚠️ 只到分钟 —— 消息列表里精确到秒没有意义，反而占地方。 */
function when(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "刚刚";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`;
  return d.toLocaleString("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function Messages() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();

  const list = useQuery({
    queryKey: ["messages"],
    queryFn: () => api.get<{ items: Message[]; nextCursor: number | null }>("/me/messages"),
    enabled: Boolean(me),
  });

  const read = useMutation({
    mutationFn: (ids?: number[]) => api.post("/me/messages/read", ids ? { ids } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["messages"] });
      void qc.invalidateQueries({ queryKey: ["messages", "unread"] });
    },
  });

  if (!me) {
    return (
      <SubPage title="消息">
        <Empty title="还没登录" hint="登录后可以收到调价提醒、发货通知" action={{ label: "去登录", to: "/login" }} />
      </SubPage>
    );
  }
  if (list.isPending) return <SubPage title="消息"><Spinner /></SubPage>;
  if (list.error) return <SubPage title="消息"><ErrorBlock error={list.error} /></SubPage>;

  const items = list.data?.items ?? [];
  const unread = items.filter((m) => !m.readAt).length;

  return (
    <SubPage
      title="消息"
      action={
        unread > 0 ? (
          <button
            className="px-1 text-[13px] text-brand-600"
            onClick={() => read.mutate(undefined)}
          >
            全部已读
          </button>
        ) : undefined
      }
    >
      {items.length === 0 ? (
        <Empty title="还没有消息" hint="上游调价、商品审核、发货、退款都会发到这里" />
      ) : (
        <div className="space-y-2 px-3 py-3">
          {items.map((m) => {
            const Icon = ICON[m.kind] ?? PackageCheck;
            return (
              <Card
                key={m.id}
                className={
                  "flex cursor-pointer gap-3 px-4 py-3.5 active:bg-secondary " +
                  (m.readAt ? "" : "border-brand-200")
                }
                onClick={() => {
                  /**
                   * ⚠️ **先标已读再跳。** 反过来的话页面已经切走，
                   *    请求可能被打断，红点会一直挂着。
                   */
                  if (!m.readAt) read.mutate([m.id]);
                  if (m.link) navigate({ to: m.link, search: {} as never });
                }}
              >
                <span
                  className={
                    "flex size-9 shrink-0 items-center justify-center rounded-lg " +
                    (TONE[m.kind] ?? "bg-secondary text-muted-foreground")
                  }
                >
                  <Icon className="size-[18px]" />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <p
                      className={
                        "min-w-0 flex-1 truncate text-[15px] " + (m.readAt ? "" : "font-medium")
                      }
                    >
                      {m.title}
                    </p>
                    {/* 🔴 未读要一眼看得出来 —— 列表里全是灰字的话他不知道哪条是新的 */}
                    {!m.readAt && <span className="size-2 shrink-0 rounded-full bg-destructive" />}
                  </div>
                  {m.body && (
                    <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-muted-foreground break-anywhere">
                      {m.body}
                    </p>
                  )}
                  <p className="mt-1 text-[12px] text-muted-foreground/70">{when(m.createdAt)}</p>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function MessagesRoute() {
  return useIsDesktop() ? <DesktopMessages /> : <Messages />;
}

export const messagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/messages",
  component: MessagesRoute,
});
